import { useCallback, useMemo } from "react";

import { useOptionalGroupRegistry } from "./shared/groups/GroupRegistry.context";
import { reportGroupRegistryError } from "./shared/groups/groupRegistry.repository";
import { resolveTemplateGroupId } from "./shared/groups/templateWorkspaceGroup";
import { useWorkspaceRepository } from "./shared/workspace/WorkspaceRepository.context";
import { useWorkspaceSelection } from "./shared/workspace/workspaceSelection.context";
import { buildWorkspaceSelectionUrl } from "./shared/workspace/workspaceSelection.url";
import type { WorkspaceTemplateId } from "./shared/workspace/workspaceTemplates";

export type WorkspaceTabId = string;

export interface WorkspaceTab {
  id: WorkspaceTabId;
  label: string;
}

export const useWorkspaceController = () => {
  const repository = useWorkspaceRepository();
  const groupRegistry = useOptionalGroupRegistry();
  const { document } = repository;
  const { activeView, selectOverview, selectWorkspace } =
    useWorkspaceSelection();

  const tabs = useMemo(
    () =>
      document.workspaces.map((entry) => ({
        id: entry.id,
        label: entry.name,
      })),
    [document],
  );
  const canCloseTab = document.workspaces.length > 1;

  const createWorkspace = useCallback(
    async (templateId?: WorkspaceTemplateId) => {
      // The group must reach the registry before the workspace commit: the new
      // tab mirrors the registry on mount and clears membership for any letter
      // the registry does not yet know.
      const selection = templateId
        ? await resolveTemplateGroupId(groupRegistry)
        : undefined;
      let entry: Awaited<ReturnType<typeof repository.createWorkspace>>;
      try {
        entry = await repository.createWorkspace(
          templateId,
          selection ? { groupId: selection.groupId } : undefined,
        );
      } catch (error) {
        // Release the letter this call allocated so a failed commit does not
        // leak it and force later templates onto the saturated fallback group.
        if (selection?.created) {
          await groupRegistry
            ?.deleteGroup(selection.groupId)
            .catch(reportGroupRegistryError);
        }
        throw error;
      }
      selectWorkspace(entry.id);
    },
    [groupRegistry, repository, selectWorkspace],
  );

  const duplicateWorkspace = useCallback(
    async (tabId: WorkspaceTabId) => {
      const result = await repository.duplicateWorkspace(tabId);
      if (result.status === "duplicated") {
        selectWorkspace(result.entry.id);
      }
      return result;
    },
    [repository, selectWorkspace],
  );

  const closeWorkspace = useCallback(
    (tabId: WorkspaceTabId) => repository.closeWorkspace(tabId),
    [repository],
  );

  const renameWorkspace = useCallback(
    (tabId: WorkspaceTabId, name: string) =>
      repository.renameWorkspace(tabId, name),
    [repository],
  );

  const openWorkspace = useCallback((tabId: WorkspaceTabId) => {
    const opened = window.open(
      buildWorkspaceSelectionUrl(window.location, tabId),
      "_blank",
      "noopener,popup=yes",
    );
    if (opened) {
      opened.opener = null;
    }
  }, []);

  const reorderWorkspace = useCallback(
    async (sourceTabId: WorkspaceTabId, targetTabId: WorkspaceTabId) => {
      await repository.reorderWorkspace(sourceTabId, targetTabId);
    },
    [repository],
  );

  const resetToDefaults = useCallback(async () => {
    await repository.reset("defaults");
  }, [repository]);

  const resetToBlank = useCallback(async () => {
    await repository.reset("blank");
  }, [repository]);

  return {
    activeView,
    selectOverview,
    selectWorkspace,
    tabs,
    canCloseTab,
    createWorkspace,
    duplicateWorkspace,
    closeWorkspace,
    renameWorkspace,
    openWorkspace,
    reorderWorkspace,
    resetToDefaults,
    resetToBlank,
  };
};
